import { useState } from "react";
import { Badge, Banner, Button, Card, Field, Page, Spinner, Table, type Column } from "../../components";
import {
  useConnectorSwitches,
  usePurge,
  useSetConnectorSwitch,
  useVendorAlerts,
  useVendorLimits,
} from "../../api/hooks";
import {
  formatDate,
  formatDateTime,
  type ConnectorSwitch,
  type PurgeCount,
  type VendorAlert,
  type VendorLimit,
} from "../../api/types";
import { t } from "../../i18n/t";
import { formatLimit, noteOf, systemLabel } from "./vendorLimits";

/**
 * §B6.5, redesigned as Settings.html: a section subnav on the left, then four cards — connector switches
 * (the one read-write table), vendor alerts and vendor limits (read-only), and the retention sweep.
 *
 * §B6.8: a switch `PUT` carries a **required** reason, enforced by the API (`min_length=3`) and mirrored here
 * so the operator is told before the round trip, not after it. The redesign draws ONE reason field per card,
 * not one per row: clicking a row's switch stages that row (the strip's Apply then names the flip), clicking
 * it again unstages it. §B6.4: the global retention sweep with a dry-run toggle that shows the `PurgeCount`
 * rows before acting — and **no per-migration purge dialog**, because endpoint 46 does not exist (§Z12).
 */
export function SettingsScreen(): JSX.Element {
  const switches = useConnectorSwitches();
  const alerts = useVendorAlerts();
  const limits = useVendorLimits();
  const setSwitch = useSetConnectorSwitch();
  const purge = usePurge();

  const [staged, setStaged] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [counts, setCounts] = useState<PurgeCount[] | null>(null);
  const [purgeStarted, setPurgeStarted] = useState(false);
  const [section, setSection] = useState("sw");

  const keyOf = (row: ConnectorSwitch) => `${row.system}/${row.entity}/${row.direction}`;
  const rows = switches.data?.items ?? [];
  const stagedRow = rows.find((row) => keyOf(row) === staged) ?? null;

  const switchColumns: readonly Column<ConnectorSwitch>[] = [
    {
      key: "system",
      header: t("settings.col_system"),
      render: (row) => <span className="cellvendor__name">{systemLabel(row.system)}</span>,
    },
    { key: "entity", header: t("settings.col_entity"), render: (row) => row.entity },
    {
      key: "direction",
      header: t("settings.col_direction"),
      render: (row) => <span className="mono">{row.direction}</span>,
    },
    {
      key: "enabled",
      header: t("settings.col_enabled"),
      render: (row) => (
        <button
          type="button"
          role="switch"
          aria-checked={row.enabled}
          aria-label={t("settings.switch_toggle", {
            system: systemLabel(row.system),
            entity: row.entity,
            direction: row.direction,
          })}
          className={row.enabled ? "sw sw--on" : "sw"}
          onClick={() => setStaged((prev) => (prev === keyOf(row) ? null : keyOf(row)))}
        >
          <span aria-hidden="true" />
        </button>
      ),
    },
    {
      key: "reason",
      header: t("settings.col_reason"),
      render: (row) =>
        row.reason === null || row.reason === "" ? (
          <span className="muted">—</span>
        ) : (
          // Settings.html's cells never wrap: the table scrolls instead (a wrapping reason would
          // double the row's height at 390 px).
          row.reason
        ),
    },
  ];

  const alertColumns: readonly Column<VendorAlert>[] = [
    {
      key: "system",
      header: t("settings.col_target"),
      render: (row) => <span className="cellvendor__name">{systemLabel(row.system)}</span>,
    },
    // `VendorAlertOut` is `{system, kind, header_value, count, first_seen, last_seen}` - there is no
    // `severity`, `message` or `created_at` column on `vendor_alerts` (§A8 row 52).
    {
      key: "severity",
      header: t("settings.col_severity"),
      render: (row) => <Badge tone="warn">{row.kind}</Badge>,
    },
    {
      key: "message",
      header: t("settings.col_message"),
      render: (row) => row.header_value,
    },
    {
      key: "created",
      header: t("settings.col_when"),
      render: (row) => <span className="mono muted">{formatDateTime(row.last_seen)}</span>,
    },
  ];

  // (v2-L-4) `vendor_limits.value` is a JSONB object literally named `value`. It is not money and is not a
  // number, but it is not shown as raw JSON either -- `formatLimit` (vendorLimits.ts) turns
  // `{limit: 100}` under `per_minute` into "100 requests / minute" for the default view. The technical
  // detail table below the fold is the one place the raw JSON still appears, and only on request.
  const limitColumns: readonly Column<VendorLimit>[] = [
    {
      key: "system",
      header: t("settings.col_system"),
      render: (row) => <span className="cellvendor__name">{systemLabel(row.system)}</span>,
    },
    {
      key: "limit",
      header: t("settings.col_limit"),
      render: (row) => <span className="mono">{formatLimit(row.key, row.value)}</span>,
    },
    {
      key: "why",
      header: t("settings.col_why"),
      render: (row) => <span className="muted">{noteOf(row.value)}</span>,
    },
  ];

  const detailColumns: readonly Column<VendorLimit>[] = [
    { key: "system", header: t("settings.col_system"), render: (row) => row.system },
    { key: "key", header: t("settings.col_target"), render: (row) => row.key },
    { key: "value", header: t("settings.col_count"), render: (row) => JSON.stringify(row.value) },
    // `VendorLimitOut` carries `effective_from`/`effective_to`, not `updated_at`.
    { key: "updated", header: t("settings.col_when"), render: (row) => formatDate(row.effective_from) },
  ];

  const sections = [
    ["sw", t("settings.switches")],
    ["al", t("settings.alerts")],
    ["li", t("settings.limits")],
    ["pu", t("settings.purge")],
  ] as const;

  return (
    <Page eyebrow={t("operator.eyebrow")} title={t("settings.title")}>
      <div className="settings">
        <nav className="subnav" aria-label={t("settings.sections")}>
          {sections.map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              aria-current={section === id ? "location" : undefined}
              onClick={() => setSection(id)}
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="settings__main">
          <Card
            flush
            title={t("settings.switches")}
            aside={<span className="card__caption">{t("settings.switches_caption")}</span>}
          >
            <div id="sw">
              {switches.isPending ? (
                <Spinner />
              ) : (
                <Table
                  bare
                  caption={t("settings.switches")}
                  columns={switchColumns}
                  rows={rows}
                  rowKey={keyOf}
                  emptyMessage={t("settings.no_switches")}
                  rowClassName={(row) => (row.enabled ? undefined : "is-fail")}
                />
              )}
            </div>
            <div className="switchform">
              <Field label={t("settings.switch_reason")} hint={t("settings.switch_reason_hint")}>
                {({ id, describedBy }) => (
                  <input
                    id={id}
                    aria-describedby={describedBy}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                )}
              </Field>
              <Button
                variant="primary"
                className="switchform__apply"
                disabled={stagedRow === null || reason.trim().length < 3 || setSwitch.isPending}
                onClick={() => {
                  if (stagedRow === null) return;
                  setSwitch.mutate(
                    {
                      system: stagedRow.system,
                      entity: stagedRow.entity,
                      direction: stagedRow.direction,
                      body: { enabled: !stagedRow.enabled, reason: reason.trim() },
                    },
                    {
                      onSuccess: () => {
                        setStaged(null);
                        setReason("");
                      },
                    },
                  );
                }}
              >
                {t("settings.switch_apply")}
              </Button>
            </div>
          </Card>

          <Card
            flush
            title={t("settings.alerts")}
            aside={<Badge tone="neutral">{t("settings.read_only")}</Badge>}
          >
            <div id="al">
              {alerts.isPending ? (
                <Spinner />
              ) : (
                <Table
                  bare
                  caption={t("settings.alerts")}
                  columns={alertColumns}
                  rows={alerts.data?.items ?? []}
                  rowKey={(row) => row.id}
                  emptyMessage={t("settings.no_alerts")}
                />
              )}
            </div>
          </Card>

          <Card
            flush
            title={t("settings.limits")}
            aside={<Badge tone="neutral">{t("settings.read_only")}</Badge>}
          >
            <div id="li">
              {limits.isPending ? (
                <Spinner />
              ) : (
                <Table
                  bare
                  caption={t("settings.limits")}
                  columns={limitColumns}
                  rows={limits.data?.items ?? []}
                  rowKey={(row) => `${row.system}/${row.key}`}
                  emptyMessage={t("settings.no_limits")}
                />
              )}
            </div>
            <details className="card__details">
              <summary>{t("settings.limits_show_detail")}</summary>
              <p className="card__details-note">{t("settings.limits_detail_caption")}</p>
              <Table
                bare
                caption={t("settings.limits_detail_caption")}
                columns={detailColumns}
                rows={limits.data?.items ?? []}
                rowKey={(row) => `${row.system}/${row.key}`}
                emptyMessage={t("settings.no_limits")}
              />
            </details>
          </Card>

          <Card flush title={t("settings.purge")}>
            <div id="pu" className="card__body">
              <label className="checkline">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(event) => setDryRun(event.target.checked)}
                />
                {t("settings.purge_dry_run")}
              </label>
              {purgeStarted ? <Banner tone="info">{t("settings.purge_started")}</Banner> : null}
              {counts === null ? null : (
                <div className="tableframe">
                  <Table
                    bare
                    visibleCaption
                    caption={t("settings.purge_counts")}
                    columns={[
                      {
                        key: "target",
                        header: t("settings.col_target"),
                        render: (row: PurgeCount) => row.target,
                      },
                      {
                        key: "count",
                        header: t("settings.col_count"),
                        render: (row: PurgeCount) => String(row.count),
                        numeric: true,
                      },
                    ]}
                    rows={counts}
                    rowKey={(row) => row.target}
                  />
                </div>
              )}
              <div>
                <Button
                  disabled={purge.isPending}
                  onClick={() => {
                    purge.mutate(
                      { dry_run: dryRun },
                      {
                        onSuccess: (result) => {
                          setCounts(result.counts ?? []);
                          setPurgeStarted(!dryRun);
                        },
                      },
                    );
                  }}
                >
                  {t("settings.purge_run")}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Page>
  );
}
